import { Button, Icon, Spinner2 } from 'components';
import { theme, Api, getCurrencySign, titleToLocaleKey, handleSubscriptionError, handleSubscriptionSuccess } from 'lib';
import { parseSubscriptionError, getErrorActionButton } from 'lib/subscriptionErrors';
import { validatePlanChange } from 'lib/subscriptionPlans';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkStatusPath } from './checkStatusPath';
import ChangePlanView from './components/ChangePlanView';
import { VISIBLE_PAYMENT_METHODS } from './constants';

// ---------------------------------------------------------------------------
// Payment provider override
// REACT_APP_PAYMENT_PROVIDER_OVERRIDE=stripe  → always use Stripe
// REACT_APP_PAYMENT_PROVIDER_OVERRIDE=komoju  → always use Komoju
// REACT_APP_PAYMENT_PROVIDER_OVERRIDE=auto    → IP-based (JP → komoju, others → stripe)
// If unset / empty                            → always use Stripe (default)
// ---------------------------------------------------------------------------
const PAYMENT_PROVIDER_OVERRIDE = process.env.REACT_APP_PAYMENT_PROVIDER_OVERRIDE?.toLowerCase().trim() || 'stripe';

/**
 * Resolves which payment provider to use.
 * @param {string|null} country - ISO-3166-1 alpha-2 country code from geo IP
 * @returns {string} provider name (e.g. 'stripe', 'komoju')
 */
const VISIBLE_PROVIDERS = new Set(VISIBLE_PAYMENT_METHODS.map((method) => method.provider));

const resolveProvider = (country) => {
  const provider =
    PAYMENT_PROVIDER_OVERRIDE === 'auto' ? (country === 'JP' ? 'komoju' : 'stripe') : PAYMENT_PROVIDER_OVERRIDE;
  console.info(`[PaymentProvider] ${PAYMENT_PROVIDER_OVERRIDE}: country=${country ?? 'unknown'} → ${provider}`);
  return VISIBLE_PROVIDERS.has(provider) ? provider : 'stripe';
};

const PaymentMethodSelection = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const { isMobile } = useSelector((state) => state.appState);

    // Basic states
    const [isProcessing, setIsProcessing] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState(true);
    const [planData, setPlanData] = useState(null);
    const [planError, setPlanError] = useState(null);
    const [teamName, setTeamName] = useState(null);
    const [detectedCountry, setDetectedCountry] = useState(null);
    const [detectedCurrency, setDetectedCurrency] = useState(null);

    // Subscription status states
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [flow, setFlow] = useState(null); // 'subscribe' | 'change-plan' | 'already-subscribed' | 'cancelled-grace-period'
    const [showProviderMismatchModal, setShowProviderMismatchModal] = useState(false);

    // Check subscription status - determines which endpoint to use
    const checkSubscriptionStatus = useCallback(async () => {
        const planId = searchParams.get('planId');
        const teamId = searchParams.get('teamId');
        const interval = searchParams.get('interval');

        if (!planId || !teamId) {
            setCheckingStatus(false);
            setFlow('subscribe'); // No teamId means new subscription
            return;
        }

        try {
            const res = await Api.get(checkStatusPath({ teamId, planId, interval }));
            
            // Handle error response format (backend returns error when upgrade is needed)
            if (res?.err && (res.err.includes('upgrade') || res.err.includes('change'))) {
                // Fetch current subscription details
                try {
                    const subRes = await Api.get('/subscriptionPlans/status');
                    let currentSub = null;

                    if (subRes?.data) {
                        const items = Array.isArray(subRes.data) ? subRes.data : [subRes.data];
                        currentSub = items.find(
                            (s) => s.teamId === teamId && (s.status === 'active' || s.status === 'success')
                        );
                    }

                    setSubscriptionStatus({
                        hasSubscription: true,
                        canSubscribe: false,
                        canUpgrade: true,
                        canDowngrade: false,
                        isSamePlan: false,
                        message: 'You can upgrade to this plan.',
                        currentSubscription: currentSub ? {
                            planTitle: currentSub.planTitle || 'Current Plan',
                            amount: currentSub.amount,
                            currency: currentSub.currency || 'usd',
                            interval: currentSub.billingCycle || currentSub.interval,
                            status: currentSub.status,
                            currentPeriodEnd: currentSub.currentPeriodEnd
                        } : null,
                        requestedPlan: null // Will be filled when plan data loads
                    });
                    setFlow('change-plan');
                } catch (err) {
                    setFlow('subscribe');
                }

                setCheckingStatus(false);
                return;
            }

            // Handle standard success response format
            if (res?.data) {
                setSubscriptionStatus(res.data);

                // Determine flow based on status.
                if (res.data.isCancelledGracePeriod) {
                    setFlow('cancelled-grace-period');
                } else if (res.data.isSamePlan) {
                    setFlow('already-subscribed');
                } else if (!res.data.hasSubscription) {
                    setFlow('subscribe');
                } else if (res.data.canUpgrade || res.data.canDowngrade) {
                    setFlow('change-plan');
                } else if (res.data.hasSubscription) {
                    setFlow('no-plan-change');
                } else {
                    setFlow('subscribe'); // Fallback
                }
            } else {
                // No status found, assume new subscription
                setFlow('subscribe');
            }
        } catch (error) {
            setFlow('subscribe'); // Default to subscribe on error
        } finally {
            setCheckingStatus(false);
        }
    }, [searchParams]);

    useEffect(() => {
        const fetchPlan = async () => {
            const planId = searchParams.get('planId');
            const interval = searchParams.get('interval');
            const teamId = searchParams.get('teamId');

            if (!planId || !interval) {
                navigate('/');
                return;
            }

            // IMPORTANT: Check subscription status FIRST
            if (teamId) {
                await checkSubscriptionStatus();

                const teamRes = await Api.get(`/subscriptionPlans/byTeam?teamId=${teamId}`);
                if (teamRes?.data?.teamName) {
                    setTeamName(teamRes.data.teamName);
                }
            } else {
                setCheckingStatus(false);
                setFlow('subscribe');
            }

            // Detect user's geo to show prices in their local currency
            let displayCurrency = 'jpy';
            try {
                const geoRes = await Api.get('/geo/detect');
                if (geoRes?.data?.country) setDetectedCountry(geoRes.data.country);
                if (geoRes?.data?.currency) {
                    displayCurrency = geoRes.data.currency;
                    setDetectedCurrency(geoRes.data.currency);
                }
            } catch { /* use default jpy */ }

            const res = await Api.get(`/subscriptionPlans/${planId}?displayCurrency=${displayCurrency}`);

            if (!res || !res.data) {
                setPlanError(true);
                setLoadingPlan(false);
                return;
            }

            const plan = res.data;
            const priceObj = plan.prices?.find(p => p.interval === interval) || plan.prices?.[0];
            const rawAmount = Math.round(priceObj?.amount) || 0;
            const displayAmount = priceObj?.discount
                ? Math.round(rawAmount * (1 - priceObj.discount / 100))
                : rawAmount;
            const planDetails = {
                tier: plan.title,
                name: t(`subscription.plans.${titleToLocaleKey(plan.title)}`, { defaultValue: plan.title }),
                price: `${getCurrencySign(priceObj?.currency) || '$'}${displayAmount}`,
                period: interval === 'monthly' ? t('paymentMethod.month') : t('paymentMethod.year'),
                features: plan.features?.map(f => {
                    const resolvedMultiplier = f.key?.includes('xpoints-earned')
                        ? (plan.pointMultiplier || Number(f.key.match(/^(\d+(?:\.\d+)?)xpoints-earned$/)?.[1]))
                        : undefined;
                    return t(`subscription.features.${f.key}`, {
                        defaultValue: f.title,
                        ...(resolvedMultiplier ? { multiplier: resolvedMultiplier } : {})
                    });
                }) || [],
                rawAmount: displayAmount,
                currency: priceObj?.currency
            };

            setPlanData(planDetails);

            // Update subscription status with requested plan details if upgrade/downgrade scenario
            if (subscriptionStatus && (subscriptionStatus.canUpgrade || subscriptionStatus.canDowngrade)) {
                setSubscriptionStatus(prev => ({
                    ...prev,
                    requestedPlan: {
                        title: plan.title,
                        monthlyPrice: interval === 'monthly' ? displayAmount : null,
                        yearlyPrice: interval === 'yearly' ? displayAmount : null,
                        currency: priceObj?.currency
                    }
                }));
            }

            setLoadingPlan(false);
        };

        fetchPlan();
    }, [searchParams, t, navigate, checkSubscriptionStatus]);

    // For NEW subscriptions (no existing subscription)
    const initiateSubscription = useCallback(async (provider, paymentMethod, currency) => {
        const planId = searchParams.get('planId');
        const interval = searchParams.get('interval');
        const teamId = searchParams.get('teamId');

        const body = { planId, teamId, interval };
        if (paymentMethod) {
            body.paymentMethod = paymentMethod;
        }
        if (currency) {
            body.currency = currency;
        }

        try {
            const res = await Api.post(`/subscriptionPlans/${provider}/subscribe`, body);

            // Check if backend handled this as an upgrade (even though we called /subscribe)
            if (res && res.data && res.data.upgraded === true) {
                // Backend detected existing subscription and upgraded it
                dispatch({
                    type: 'HANDLE_RES',
                    payload: { res: res.data.message || t('subscription.upgradeSuccessful') || 'Subscription upgraded successfully!' }
                });

                // Redirect to success page with parameters
                const sessionId = res.data?.sessionId || res.data?.session_id || 'upgraded';
                const planTitle = planData?.name || planData?.tier || 'Subscription';

                setTimeout(() => {
                    navigate(`/subscription/success?session_id=${sessionId}&title=${encodeURIComponent(planTitle)}&teamName=${encodeURIComponent(teamName || '')}&teamId=${teamId || ''}`);
                }, 1000);
            } else if (res && res.data && res.data.checkoutUrl) {
                // Normal checkout flow - redirect to Stripe
                window.location.href = res.data.checkoutUrl;
            } else if (res && res.err) {
                // Handle error response - error message already dispatched by Api.handleError
                // Just stop processing
                setIsProcessing(false);
            } else if (res === false) {
                // API returned false - error was already dispatched by Api.handleError
                // Don't dispatch again, just stop processing
                setIsProcessing(false);
            } else {
                // Unknown response format
                const errorMessage = res?.err || t('subscription.upgradeFailed') || 'Something went wrong. Please try again.';
                dispatch({
                    type: 'HANDLE_RES',
                    payload: { err: errorMessage }
                });
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Subscription initiation failed:', error);
            // Error already handled by Api.handleError, don't dispatch again
            setIsProcessing(false);
        }
    }, [searchParams, dispatch, navigate, t, setIsProcessing, teamName, planData]);

    // For UPGRADES/DOWNGRADES (existing subscription, different plan)
    const handleChangePlan = useCallback(async () => {
        const planId = searchParams.get('planId');
        const teamId = searchParams.get('teamId');
        const interval = searchParams.get('interval');

        setIsProcessing(true);

        try {
            let country = detectedCountry;
            let currency = detectedCurrency;

            // Fallback: if geo wasn't detected during page load, try again
            if (!country) {
                const geoRes = await Api.get('/geo/detect');
                country = geoRes?.data?.country;
                currency = geoRes?.data?.currency;
            }

            // Determine which provider the current subscription was made with,
            // and which provider the user's current geo location maps to.
            // Komoju is used for Japan (jpy); Stripe is used everywhere else.
            // REACT_APP_PAYMENT_PROVIDER_OVERRIDE can force a single provider site-wide.
            const currentSubCurrency = subscriptionStatus?.currentSubscription?.currency?.toLowerCase();
            const currentSubProvider = currentSubCurrency === 'jpy' ? 'komoju' : 'stripe';
            const targetProvider = resolveProvider(country);

            if (currentSubProvider !== targetProvider) {
                setIsProcessing(false);
                setShowProviderMismatchModal(true);
                return;
            }

            let res = null;

            if (targetProvider === 'komoju') {
                res = await Api.post('/subscriptionPlans/komoju/change', {
                    teamId,
                    newPlanId: planId,
                    interval: interval || 'monthly'
                });
            } else {
                res = await Api.post('/subscriptionPlans/stripe/change', {
                    teamId,
                    newPlanId: planId,
                    interval: interval || 'monthly'
                });
            }

            // Check if successful and redirect with parameters
            if (res && (res.data || res.statusCode === 200)) {
                const sessionId = res.data?.sessionId || res.data?.session_id || 'upgraded';
                const planTitle = planData?.name || planData?.tier || 'Subscription';

                navigate(`/subscription/success?session_id=${sessionId}&title=${encodeURIComponent(planTitle)}&teamName=${encodeURIComponent(teamName || '')}&teamId=${teamId || ''}`);
                return;
            }

            const wasSuccess = handleSubscriptionSuccess(res, dispatch, navigate, {
                successMessage: t('subscription.upgradeSuccessful')
            });

            if (!wasSuccess) {
                // Handle error with confirmation for certain actions
                handleSubscriptionError(res, dispatch, navigate, {
                    defaultMessage: 'An error occurred',
                    confirmRedirect: true,
                    confirmActions: ['update_payment', 'contact_support']
                });
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Change plan failed:', error);
            handleSubscriptionError(error, dispatch, navigate, {
                defaultMessage: t('subscription.upgradeFailed')
            });
            setIsProcessing(false);
        }
    }, [searchParams, dispatch, navigate, t, teamName, planData]);

    // Handler for payment button - routes to correct endpoint based on flow
    const handlePaymentClick = useCallback(async () => {
        // If it's a plan change (upgrade/downgrade), use change endpoint
        if (flow === 'change-plan') {
            await handleChangePlan();
            return;
        }

        // Otherwise, proceed with normal subscription flow
        setIsProcessing(true);
        try {
            let country = detectedCountry;
            let currency = detectedCurrency;

            // Fallback: if geo wasn't detected during page load, try again
            if (!country) {
                const geoRes = await Api.get('/geo/detect');
                country = geoRes?.data?.country;
                currency = geoRes?.data?.currency;
            }

            const provider = resolveProvider(country);
            if (provider === 'komoju') {
                await initiateSubscription('komoju', null, null);
            } else {
                await initiateSubscription('stripe', null, currency || 'usd');
            }
        } catch {
            await initiateSubscription('stripe', null, null);
        }
    }, [flow, handleChangePlan, detectedCountry, detectedCurrency, initiateSubscription]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Show loading while checking status or loading plan
    if (checkingStatus || loadingPlan) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <div className="text-center">
                    <Spinner2 col={theme.SECONDARY} />
                    <p className="mt-4 text-gray-500">
                        {checkingStatus ? t('subscription.checkingStatus') : t('common.loading')}
                    </p>
                </div>
            </div>
        );
    }

    if (planError) {
        return (
            <div className="max-w-[480px] mx-auto p-4 pt-10 text-center">
                <Icon type="warning" dimensions={40} col={theme.RED} />
                <h3 className="text-xl font-bold my-5">{t('paymentMethod.planNotFound')}</h3>
                <Button text={t('paymentMethod.backToPlans')} onClick={() => navigate(-1)} />
            </div>
        );
    }

    // State 1: Already subscribed to same plan
    if (flow === 'already-subscribed' && subscriptionStatus) {
        return (
            <div className={` bg-white ${!isMobile ? 'flex items-center justify-center bg-gray-50 mt-28' : 'mt-4'}`}>
                <div className={`w-full max-w-[550px] bg-white ${!isMobile ? 'min-h-[600px] overflow-hidden relative rounded-lg shadow-lg' : 'max-w-[unset]'}`}>
                    <div className="flex items-center p-4 border-b border-gray-100">
                        <div onClick={() => navigate(-1)} className="cursor-pointer p-2 -ml-2 hover:bg-gray-50 rounded-full transition-colors">
                            <Icon type="arrowLeft" dimensions={24} col={theme.PRIMARY_BLACK} />
                        </div>
                        <div className="flex-1 text-center pr-8">
                            <h4 className="text-lg font-bold">{t('subscription.subscriptionDetails') || 'Subscription Details'}</h4>
                        </div>
                    </div>

                    <div className="p-6 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                            <Icon type="check" dimensions={32} col={theme.GREEN} />
                        </div>

                        <h3 className="text-2xl font-bold mb-2">{t('subscription.alreadySubscribed')}</h3>
                        <p className="text-gray-600 mb-6">{subscriptionStatus.message || 'You already have an active subscription for this team.'}</p>

                        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                            <h4 className="font-bold text-sm text-gray-500 uppercase mb-3">{t('subscription.currentSubscription') || 'Current Subscription'}</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('subscription.planTier') || 'Plan'}</span>
                                    <span className="font-semibold">{subscriptionStatus.currentSubscription?.planTitle}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('paymentMethod.total') || 'Amount'}</span>
                                    <span className="font-semibold">
                                        {getCurrencySign(subscriptionStatus.currentSubscription?.currency) || '$'}
                                        {subscriptionStatus.currentSubscription?.amount}/{subscriptionStatus.currentSubscription?.interval}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('subscription.status') || 'Status'}</span>
                                    <span className="font-semibold capitalize">{subscriptionStatus.currentSubscription?.status}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('subscription.nextBillingDate') || 'Next Billing'}</span>
                                    <span className="font-semibold">{formatDate(subscriptionStatus.currentSubscription?.currentPeriodEnd)}</span>
                                </div>
                            </div>
                        </div>

                        <Button
                            text={t('subscription.manageSubscription') || 'Manage Subscription'}
                            onClick={() => navigate('/billing')}
                            bg={theme.SECONDARY}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // State 2: Subscription exists but plan changes are blocked (grace period or yearly restriction)
    if ((flow === 'cancelled-grace-period' || flow === 'no-plan-change') && subscriptionStatus) {
        const isCancelledGrace = flow === 'cancelled-grace-period';
        const blockedTitle = isCancelledGrace
            ? t('subscription.cancelledGracePeriod.title')
            : t('subscription.noPlanChange.title');
        const blockedMessage = isCancelledGrace
            ? t('subscription.cancelledGracePeriod.message')
            : t('subscription.noPlanChange.message');
        const dateLabel = isCancelledGrace
            ? t('subscription.accessUntil')
            : t('subscription.nextBillingDate');

        return (
            <div className={`bg-white ${!isMobile ? 'flex items-center justify-center bg-gray-50 mt-28' : 'mt-4'}`}>
                <div className={`w-full max-w-[550px] bg-white ${!isMobile ? 'min-h-[600px] overflow-hidden relative rounded-lg shadow-lg' : 'max-w-[unset]'}`}>
                    <div className="flex items-center p-4 border-b border-gray-100">
                        <div onClick={() => navigate(-1)} className="cursor-pointer p-2 -ml-2 hover:bg-gray-50 rounded-full transition-colors">
                            <Icon type="arrowLeft" dimensions={24} col={theme.PRIMARY_BLACK} />
                        </div>
                        <div className="flex-1 text-center pr-8">
                            <h4 className="text-lg font-bold">{t('subscription.subscriptionDetails') || 'Subscription Details'}</h4>
                        </div>
                    </div>

                    <div className="p-6 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
                            <Icon type="warning" dimensions={32} col="#F59E0B" />
                        </div>

                        <h3 className="text-2xl font-bold mb-2">{blockedTitle}</h3>
                        <p className="text-gray-600 mb-6">{blockedMessage}</p>

                        {subscriptionStatus.currentSubscription && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                <h4 className="font-bold text-sm text-gray-500 uppercase mb-3">{t('subscription.currentSubscription') || 'Current Subscription'}</h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('subscription.planTier') || 'Plan'}</span>
                                        <span className="font-semibold">{subscriptionStatus.currentSubscription?.planTitle}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('paymentMethod.total') || 'Amount'}</span>
                                        <span className="font-semibold">
                                            {getCurrencySign(subscriptionStatus.currentSubscription?.currency) || '$'}
                                            {subscriptionStatus.currentSubscription?.amount} ({subscriptionStatus.currentSubscription?.interval})
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{dateLabel}</span>
                                        <span className="font-semibold">{formatDate(subscriptionStatus.currentSubscription?.currentPeriodEnd)}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Button
                            text={t('subscription.manageSubscription') || 'Manage Subscription'}
                            onClick={() => navigate('/billing')}
                            bg={theme.SECONDARY}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // State 3: Can upgrade/downgrade
    if (flow === 'change-plan' && subscriptionStatus) {
        return (
            <div className={`bg-white ${!isMobile ? 'flex items-center justify-center bg-gray-50 mt-28' : 'mt-4'}`}>

                {showProviderMismatchModal && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 max-w-[400px] w-full shadow-xl">
                            <div className="text-center mb-4">
                                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <Icon type="warning" dimensions={28} col="#F59E0B" />
                                </div>
                                <h3 className="text-lg font-bold mb-2">
                                    {t('subscription.providerMismatch.title') || 'Cannot Change Plan'}
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {t('subscription.providerMismatch.message') ||
                                        'Your current subscription was purchased through a different payment provider. Please cancel your current subscription first, then purchase the new plan.'}
                                </p>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setShowProviderMismatchModal(false)}
                                    className="flex-1 bg-gray-200 text-gray-700 rounded-full h-12 text-sm font-bold hover:bg-gray-300 transition-colors"
                                >
                                    {t('common.cancel') || 'Cancel'}
                                </button>
                                <button
                                    onClick={() => navigate('/billing?tab=1')}
                                    className="flex-1 bg-black text-white rounded-full h-12 text-sm font-bold hover:bg-gray-800 transition-colors"
                                >
                                    {t('subscription.manageBilling') || 'Manage Billing'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`w-full max-w-[550px] bg-white ${!isMobile ? 'min-h-[600px] relative' : 'max-w-[unset]'}`}>
                    <ChangePlanView
                        canUpgrade={!!subscriptionStatus.canUpgrade}
                        message={subscriptionStatus.message}
                        currentPlan={
                            subscriptionStatus.currentSubscription && {
                                name: subscriptionStatus.currentSubscription.planTitle,
                                price: `${getCurrencySign(subscriptionStatus.currentSubscription.currency) || '$'}${subscriptionStatus.currentSubscription.amount}`,
                                period: subscriptionStatus.currentSubscription.interval === 'yearly'
                                    ? t('paymentMethod.year')
                                    : t('paymentMethod.month')
                            }
                        }
                        isIntervalChange={
                            !!subscriptionStatus.currentSubscription?.planId &&
                            subscriptionStatus.currentSubscription.planId === searchParams.get('planId')
                        }
                        newPlan={{ name: planData?.name, price: planData?.price, period: planData?.period }}
                        features={planData?.features}
                        isProcessing={isProcessing}
                        isMobile={isMobile}
                        onCancel={() => navigate(-1)}
                        onConfirm={handleChangePlan}
                    />
                </div>
            </div>
        );
    }

    // State 4: Normal subscription flow
    return (
        <div className={` bg-white ${!isMobile ? 'flex items-center justify-center bg-gray-50 mt-28' : 'mt-4'}`}>
            <div className={`w-full max-w-[550px] bg-white ${!isMobile ? 'min-h-[600px] overflow-hidden relative' : 'max-w-[unset]'}`}>
                {/* Header */}
                <div className="flex items-center p-4 border-b border-gray-100">
                    <div onClick={() => navigate(-1)} className="cursor-pointer p-2 -ml-2 hover:bg-gray-50 rounded-full transition-colors">
                        <Icon type="arrowLeft" dimensions={24} col={theme.PRIMARY_BLACK} />
                    </div>
                    <div className="flex-1 text-center pr-8">
                        <h4 className="text-lg font-bold">
                            {t('paymentMethod.subscriptionSummary')}
                        </h4>
                    </div>
                </div>

                <div className="p-6">
                    {/* Toggle (Static) */}
                    {/* TODO: Make dynamic based on actual interval selection */}
                    {/* {renderToggle()} */}

                    {/* Plan Details */}
                    {planData && (
                        <>
                            {teamName && (
                                <p className="text-sm text-gray-600 font-medium italic mb-2">{teamName}</p>
                            )}
                            <div className="flex items-baseline justify-between mb-6">
                                <h3 className="text-2xl font-bold">
                                    {planData.name}
                                </h3>
                                <h3 className="text-2xl font-bold">
                                    {planData.price}
                                </h3>
                            </div>

                            {/* Features */}
                            <div className="mb-8">
                                {planData.features.map((feature, i) => (
                                    <div key={i} className="flex items-start mb-3">
                                        <div className="mt-1.5 mr-2 w-1 h-1 rounded-full bg-black shrink-0" />
                                        <span className="text-base text-gray-900">{feature}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="w-full h-px bg-gray-100 mb-6" />

                            {/* Total */}
                            <div className="flex items-center justify-between mb-8">
                                <h2 className="text-3xl font-bold">
                                    {t('paymentMethod.total')}
                                </h2>
                                <h2 className="text-3xl font-bold">
                                    {planData.price}
                                </h2>
                            </div>

                            {/* Pay Button */}
                            <div className='mb-16'>
                                <button
                                    onClick={handlePaymentClick}
                                    className="w-full bg-black text-white rounded-full h-14 text-lg font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? <Spinner2 col="#ffffff" /> : `${t('paymentMethod.pay')} ${planData.price}`}
                                </button>
                                <p className="text-[11px] text-gray-700 text-center mt-3 leading-tight opacity-90 mx-auto">
                                    {t('subscription.hero.priceDisclaimer')}
                                </p>
                                {searchParams.get('interval') === 'yearly' && (
                                    <p className="text-[11px] text-gray-700 text-center mt-3 leading-tight opacity-90 mx-auto">
                                        {t('paymentMethod.oneTimePaymentNote')}
                                    </p>
                                )}
                            </div>

                        </>
                    )}

                </div>
            </div>
        </div>
    );
};

export default PaymentMethodSelection;
